<template>
  <div class="measurement-container-large">
    <PopoverMeasurement
      :reading="readingPm"
      :label="$t(`readings.${pmReadingType}`)"
      :unitsLabel="pmReadingUnitLabel"
      imageSrc="/img/measurements/pm.svg"
      :largeMeasurement="true"
      dataCy="pmMeasurementLarge"
    />
    <PopoverMeasurement
      :reading="readingTemp"
      :label="$t(`readings.temp`)"
      :unitsLabel="tempReadingUnitLabel"
      imageSrc="/img/measurements/temp.svg"
      :largeMeasurement="true"
      dataCy="temperatureMeasurementLarge"
    />
    <PopoverMeasurement
      :reading="readingHumidity"
      :label="$t(`readings.rh`)"
      :unitsLabel="$t('units.percent')"
      imageSrc="/img/measurements/rh.svg"
      :largeMeasurement="true"
      dataCy="rhMeasurementLarge"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import PopoverMeasurement from '../popoverMeasurement.vue'
import { getPMSelectedUnit, getTemperatureSelectedUnit } from '@/services/user-settings'
import { getPMRawMeasurementType, getReadingUnitLabel } from '@/components/devicePopover/utils'
import { getReadingPM, getReadingTemperature, getReadingValue } from '@/services/unit-conversion'
import { MeasurementType, measurementTypesKey } from '@/services/device-measurements'
import { NAMESPACES } from '@/store/types'
import { DEVICE_GETTERS } from '@/store/devices'
import { MAP_GETTERS } from '@/store/map'
import type { GetterByDeviceId } from '@/components/devicePopover/types'
import type { FormattedMeasurement, SensorReadings } from '@/services/device-service/devices'

export default defineComponent({
  name: 'measurementsLarge',
  props: {
    deviceId: { type: String, default: '' },
    sensorReadings: { type: Object as PropType<SensorReadings>, default: null }
  },
  components: {
    PopoverMeasurement
  },
  computed: {
    deviceType(): string {
      const getter = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_DEVICE_TYPE_BY_DEVICE_ID}`
      ] as GetterByDeviceId<string>
      return getter(this.deviceId)
    },
    pmReadingType(): string {
      const measType = this.$store.getters[
        `${NAMESPACES.MAP}/${MAP_GETTERS.GET_MEASUREMENT_TYPE}`
      ] as number
      return getPMRawMeasurementType(measType)
    },
    pmReadingUnit() {
      return getPMSelectedUnit()
    },
    pmReadingUnitLabel(): string {
      return getReadingUnitLabel(this.pmReadingUnit)
    },
    tempReadingUnit() {
      return getTemperatureSelectedUnit()
    },
    tempReadingUnitLabel(): string {
      return getReadingUnitLabel(this.tempReadingUnit)
    },
    readingPm(): FormattedMeasurement | null {
      return getReadingPM(
        this.sensorReadings,
        this.pmReadingType,
        this.pmReadingUnit,
        this.deviceType
      )
    },
    readingTemp(): FormattedMeasurement | null {
      return getReadingTemperature(this.sensorReadings, this.tempReadingUnit, this.deviceType)
    },
    readingHumidity(): FormattedMeasurement | null {
      return getReadingValue(
        this.sensorReadings,
        measurementTypesKey[MeasurementType.Rh],
        this.deviceType,
        true
      )
    }
  }
})
</script>
<style lang="scss" scoped>
.measurement-container-large {
  margin-top: 16px;

  @media (max-width: 460px) {
    padding-bottom: 40px;
  }
}
</style>
